// @ts-nocheck
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { OrderChangeRequestLineModel, OrderChangeRequestModel } from "@store/Framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import constants from "@store/constants";
import clearChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/ClearChangeRequestOrder";
import setChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/SetChangeRequestOrder";
import isEmpty from "lodash/isEmpty";
import React, { useContext, useEffect, useState } from "react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { PageErrorWrapper, PageErrorWrapperStyles } from "@insite/content-library/Components/PageErrorWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import OrderLayout from "@store/Library/Components/Toolkit/Layout/OrderLayout";
import NavigationBlocker from "@store/Library/Components/NavigationBlocker";
import getOrderChangeRequestHasChanges from "@store/Framework/Common/Utilities/OrderChangeRequest/getOrderChangeRequestHasChanges";
import translate from "@insite/client-framework/Translate";

export interface OrderChangeRequestPageContainerProps extends WidgetProps {}

export interface OrderChangeRequestPageContainerStyles {
    pageErrorWrapper?: PageErrorWrapperStyles;
}

export const OrderChangeRequestPageContainerStyles: OrderChangeRequestPageContainerStyles = {};

const styles = OrderChangeRequestPageContainerStyles;

const OrderChangeRequestPageContainer = ({ id }: OrderChangeRequestPageContainerProps) => {
    const dispatch = useAppDispatch();
    const orderDataView = useContext(OrderStateContext);
    const { value: order } = orderDataView;
    const { changeRequestOrder, requestSubmitted } = useAppSelector(state => state.greif.pages.orderChange);
    const [simplifiedOrder, setSimplifiedOrder] = useState<OrderChangeRequestModel>();
    const [isHydrated, setIsHydrated] = useState(false);
    const currentOrderRequestId = order ? `orderChange-${order.id}` : undefined;
    const hasChanges = isHydrated && getOrderChangeRequestHasChanges(changeRequestOrder);

    useEffect(() => {
        if (!order || !currentOrderRequestId) {
            return;
        }

        if (
            (!isEmpty(changeRequestOrder) && changeRequestOrder.orderId !== currentOrderRequestId) ||
            requestSubmitted
        ) {
            dispatch(clearChangeRequestOrder({}));
        }
    }, [changeRequestOrder, currentOrderRequestId, order, requestSubmitted]);

    useEffect(() => {
        if (!order || !currentOrderRequestId) {
            setIsHydrated(false);
            return;
        }

        const changeRequestOrderJson = sessionStorage.getItem(currentOrderRequestId);

        if (changeRequestOrderJson && changeRequestOrderJson.length > 0) {
            const changeRequestOrderObj: OrderChangeRequestModel = JSON.parse(changeRequestOrderJson);
            dispatch(setChangeRequestOrder({ changeRequestOrder: changeRequestOrderObj }));
            setIsHydrated(true);

            return;
        }

        const simplifiedLines: OrderChangeRequestLineModel[] = [];
        order?.orderLines?.forEach(line => {
            const qtyRemaining = Math.max(line.qtyOrdered - line.qtyShipped, 0);
            const isLineCanceled = line.status.toLowerCase() === "canceled";

            simplifiedLines.push({
                orderLineId: line.id,
                changeNotes: "",
                changeReason: "",
                newQuantity: "",
                originalQuantity: qtyRemaining,
                isCanceled: isLineCanceled || false,
                leadTimeInWeeks: line?.properties["leadTime"],
                plant: line.warehouse,
            } as OrderChangeRequestLineModel);
        });

        const orderChangeRequest: OrderChangeRequestModel = {
            erpNumber: order.erpOrderNumber,
            newLines: [],
            orderId: currentOrderRequestId,
            orderLines: simplifiedLines,
        };

        setSimplifiedOrder(orderChangeRequest);
        dispatch(setChangeRequestOrder({ changeRequestOrder: orderChangeRequest }));

        const stringifiedOrderRequest = JSON.stringify(orderChangeRequest);
        sessionStorage.setItem(currentOrderRequestId, stringifiedOrderRequest);
        setIsHydrated(true);
    }, [currentOrderRequestId, order]);

    useEffect(() => {
        if (!isHydrated || !changeRequestOrder || !currentOrderRequestId) {
            return;
        }

        if (changeRequestOrder.orderId !== currentOrderRequestId) {
            return;
        }

        const stringifiedOrderRequest = JSON.stringify(changeRequestOrder);
        sessionStorage.setItem(currentOrderRequestId, stringifiedOrderRequest);
    }, [changeRequestOrder, currentOrderRequestId, isHydrated]);

    useEffect(() => {
        if (simplifiedOrder && order) {
            dispatch(setChangeRequestOrder({ changeRequestOrder: simplifiedOrder }));
        }
    }, [order, simplifiedOrder]);

    useEffect(() => {
        // Clear store value on unmount
        return () => {
            dispatch(clearChangeRequestOrder({}));
        };
    }, []);

    return (
        <>
            <PageErrorWrapper
                value={orderDataView}
                badRequestMessage={siteMessage("Order_BadRequest")}
                notAuthorizedMessage={siteMessage("Order_NotAuthorized")}
                notFoundMessage={siteMessage("Order_NotFound")}
                extendedStyles={styles.pageErrorWrapper}
            >
                <OrderLayout id={id} loading={!changeRequestOrder} isV2 />
            </PageErrorWrapper>
            <NavigationBlocker
                isActive={hasChanges}
                title={siteMessage("OrderChangeRequest_ExitConfirmationHeadline")}
                text={siteMessage("OrderChangeRequest_ExitConfirmationMessage")}
                leaveLabel={translate("Cancel Change")}
                onLeave={() => {
                    if (currentOrderRequestId) {
                        sessionStorage.removeItem(currentOrderRequestId);
                    }
                    dispatch(clearChangeRequestOrder({}));
                }}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestPageContainer,
    definition: {
        allowedContexts: [constants.pageType.orderChangeRequest],
        displayName: "Order Change Request Page Container",
        group: constants.widgetGroup.orderChangeRequest,
    },
};

export default widgetModule;
